import type { EncryptedData } from '../auth/crypto';
import type { BlobStore } from './accounts';

const DB_NAME = 'terminal-nsi-xyz';
const DB_VERSION = 1;
const STORE = 'account-blobs';

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE)) database.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB indisponible'));
  });
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Requête IndexedDB échouée'));
  });
}

/** Données de compte chiffrées dans IndexedDB. */
export class IndexedDbBlobStore implements BlobStore {
  async put(id: string, blob: EncryptedData): Promise<void> {
    const database = await openDatabase();
    const transaction = database.transaction(STORE, 'readwrite');
    transaction.objectStore(STORE).put(blob, id);
    await new Promise<void>((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error ?? new Error('Écriture impossible'));
    });
    database.close();
  }

  async get(id: string): Promise<EncryptedData | null> {
    const database = await openDatabase();
    const transaction = database.transaction(STORE, 'readonly');
    const result = await requestResult(transaction.objectStore(STORE).get(id));
    database.close();
    return (result as EncryptedData | undefined) ?? null;
  }

  async remove(id: string): Promise<void> {
    const database = await openDatabase();
    const transaction = database.transaction(STORE, 'readwrite');
    transaction.objectStore(STORE).delete(id);
    await new Promise<void>((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error ?? new Error('Suppression impossible'));
    });
    database.close();
  }
}
